import {
  conditionOperatorLabelKey,
  conditionTakesLeftValue,
  conditionTakesRightValue,
  isDefaultCondition,
} from "@/flow/utils/conditionLabels";
import {
  DEFAULT_LOOP_THREAD_COUNT,
  loopTypeLabelKey,
} from "@/flow/utils/loopUtils";
import { checkIsLoop } from "@/core/util";

/**
 * Summarizes what a wizard-preview step was actually configured with, for the canvas hover card.
 *
 * Preview-only by design: values here are still in the wizard's authored form (`step_2.output.x`,
 * `{{custom_field.Y}}`, plain literals). A saved flow stores the same values encoded as
 * `[{"name":"/1/#field","type":"output"}]` and needs the step's outputList to decode, which the
 * canvas does not receive.
 */

export type InputKind = "parameter" | "header";

/** Matches the CSS the editor uses for the same reference: `.tag.output-tag` etc. */
export type ValueTagKind = "output" | "user-defined-variable" | "";

export interface DisplayValue {
  /** What the editor would show for this value — e.g. `1./count`, `.TenantId`, or the literal. */
  text: string;
  /** "" for a plain literal, which renders as text rather than a tag. */
  tagKind: ValueTagKind;
}

export interface InputRow {
  label: string;
  /** Exactly what was configured, kept for debugging and tests. */
  raw: string;
  /** The value in the editor's own notation — what the reviewer will see if they open the step. */
  display: DisplayValue;
  kind: InputKind;
}

export interface ConditionRow {
  /** "AND" / "OR" joining this row to the previous one; "" on the first row. */
  rule: string;
  openParen: boolean;
  closeParen: boolean;
  left: DisplayValue;
  /** i18n key under `Condition.*`. */
  operatorLabelKey: string;
  right: DisplayValue;
}

export interface BranchSummary {
  /** "" when the branch is unnamed — the caller supplies a translated fallback. */
  name: string;
  /** The fallback branch — rendered as "otherwise", with no rows. */
  isDefault: boolean;
  rows: ConditionRow[];
}

/**
 * True when a configured value carries nothing.
 *
 * `"[]"` is the app-wide empty sentinel for expression fields — `convertToDefinitions` in
 * `EditStep.vue` drops any key whose value is exactly that. An array of entries with blank `name`
 * is the same thing after a round trip through the editor.
 */
/** An expression field is empty when it holds no entries, or only entries with a blank `name`. */
function isBlankEntryList(entries: unknown[]): boolean {
  return (
    entries.length === 0 ||
    entries.every((entry: any) => !String(entry?.name ?? "").trim())
  );
}

export function isEmptyInputValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (Array.isArray(value)) {
    // Same rule the string branch below applies once it parses: entries with a blank `name` are
    // what an emptied expression field leaves behind, whether it arrives parsed or as JSON text.
    return isBlankEntryList(value);
  }
  if (typeof value !== "string") {
    // Numbers and booleans are legitimate values — 0 and false included.
    return false;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed === "[]") {
    return true;
  }
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return isBlankEntryList(parsed);
      }
    } catch {
      // Not JSON after all — treat it as an ordinary literal.
    }
  }
  return false;
}

export function toDisplayString(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const STEP_OUTPUT_RE = /^step_(\d+)\.output(?:\.(.+))?$/;
const CUSTOM_FIELD_RE = /^\{\{\s*custom_field\.([A-Za-z0-9.]+)\s*\}\}$/;
// The wizard's loop-item forms: bare `{{loop.item[.field]}}` and, once loops nest, the
// loop-qualified `{{loop.<loop_id>.item[.field]}}`. Mirrors _LOOP_ITEM_RE and friends in
// flow_preprocessor.py, and the bare form is tested first so a field literally named "item"
// still reads as innermost-loop + field.
const LOOP_ITEM_RE = /^\{\{\s*loop\.item(?:\.(.+?))?\s*\}\}$/;
const LOOP_ID_ITEM_RE = /^\{\{\s*loop\.[A-Za-z0-9_]+\.item(?:\.(.+?))?\s*\}\}$/;

/** Context a value needs to render exactly as the editor would. */
export interface DisplayContext {
  /**
   * Step number of the LOOP node enclosing this step, when there is one.
   *
   * A loop item lowers to an output reference on that LOOP node — `{{loop.item.asset_id}}` is
   * stored as `/2/#asset_id` — so without the number the tag can only show the path.
   */
  loopStepNumber?: number;
}

/**
 * Renders a value the way the flow editor renders it, so the hover card and the step drawer agree.
 *
 * The editor shows an output reference as `<stepNumber>./<path>` on a purple tag — `addOutput` in
 * `spanInjection.ts` builds exactly that, converting `/#` and `@#` separators to `/`. The wizard
 * authors the same reference as `step_1.output.count`, so translate rather than show its internal
 * syntax. Anything that is not a recognised reference is a literal and renders as plain text.
 */
export function toDisplayValue(
  raw: string,
  context: DisplayContext = {}
): DisplayValue {
  const trimmed = (raw ?? "").trim();

  const stepMatch = STEP_OUTPUT_RE.exec(trimmed);
  if (stepMatch) {
    const path = (stepMatch[2] ?? "").split(".").filter(Boolean).join("/");
    return { text: `${stepMatch[1]}./${path}`, tagKind: "output" };
  }

  const customFieldMatch = CUSTOM_FIELD_RE.exec(trimmed);
  if (customFieldMatch) {
    return {
      text: `.${customFieldMatch[1]}`,
      tagKind: "user-defined-variable",
    };
  }

  // A loop item is an output reference on the enclosing LOOP node, not a literal: the preprocessor
  // lowers `{{loop.item.asset_id}}` to `{"name": "…@#asset_id", "type": "output"}`, which the
  // editor draws on its purple output tag. Showing the raw `{{…}}` here made the preview disagree
  // with the step drawer for every step inside a loop.
  const loopMatch = LOOP_ITEM_RE.exec(trimmed) ?? LOOP_ID_ITEM_RE.exec(trimmed);
  if (loopMatch) {
    const field = (loopMatch[1] ?? "").split(".").filter(Boolean).join("/");
    // The LOOP node stores the iterated item as its own output, so a FIELD of the item is addressed
    // directly in it (`/2/#asset_id`). Only the whole item carries the `output` segment, which is
    // the `{"output":<value>}` wrapper the engine puts around a scalar item (AI-301).
    const path = field || "output";
    const prefix = context.loopStepNumber ? `${context.loopStepNumber}.` : "";
    return { text: `${prefix}/${path}`, tagKind: "output" };
  }

  return { text: raw, tagKind: "" };
}

/**
 * CSS classes for one rendered value, so every card shows the same value the same way.
 *
 * A reference reuses the editor's own tag classes (`tag output-tag`, `tag user-defined-variable-tag`)
 * so it looks identical here and in the step drawer. A literal has no tag colour of its own, so it
 * gets the neutral chip the JSON-tree tooltip uses for its path segments.
 */
export function displayValueClass(display: DisplayValue): string[] {
  return display.tagKind
    ? ["tag", `${display.tagKind}-tag`, "node-summary-tag"]
    : ["node-summary-literal"];
}

function toInputRows(
  source: unknown,
  kind: InputKind,
  context: DisplayContext
): InputRow[] {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return [];
  }

  return Object.entries(source as Record<string, unknown>)
    .filter(([, value]) => !isEmptyInputValue(value))
    .map(([label, value]) => {
      const raw = toDisplayString(value);
      return { label, raw, display: toDisplayValue(raw, context), kind };
    });
}

/** Filled parameters then filled headers, in authored order. Empty fields are dropped. */
export function summarizeStepInputs(step: any): InputRow[] {
  const context: DisplayContext = {
    loopStepNumber:
      typeof step?.loopStepNumber === "number"
        ? step.loopStepNumber
        : undefined,
  };

  return [
    ...toInputRows(step?.parameters, "parameter", context),
    ...toInputRows(step?.headers, "header", context),
  ];
}

/**
 * A LOOP node, recognised the way the rest of the app recognises one.
 *
 * Both halves are needed: a saved flow marks the node with `type`, while `checkIsLoop` reads the
 * `uiData.loop` flag some steps carry instead. `nodeUtils` pairs them the same way, so a card that
 * checked only `type` would miss loops every other part of the canvas sees.
 */
export function isLoopStep(step: any): boolean {
  return step?.type === "LOOP" || checkIsLoop(step);
}

export interface LoopRow {
  /** i18n key for the row label, or "" when the value stands on its own line without one. */
  labelKey: string;
  /**
   * Rendered value; a reference is tagged, everything else is plain. When `translateValue` is set
   * the text is itself an i18n key the component translates.
   */
  display: DisplayValue;
  /** The value is an i18n key, not literal text — used for the loop type's own label. */
  translateValue?: boolean;
}

/**
 * A LOOP node's own settings, for its hover card.
 *
 * A loop carries no parameters or headers, so the inputs card is always empty for one — yet it is
 * the node whose configuration is least visible on the canvas, since what it iterates over and
 * whether it runs in parallel live behind the Loop Settings drawer.
 */
export function summarizeLoop(step: any): LoopRow[] {
  if (!isLoopStep(step)) {
    return [];
  }

  const rows: LoopRow[] = [];

  // What kind of loop this is, in the drawer's own words ("Loop through each item"). Stated on its
  // own line rather than as a name/value pair, matching how the drawer presents it.
  rows.push({
    labelKey: "",
    display: { text: loopTypeLabelKey(step?.loopType), tagKind: "" },
    translateValue: true,
  });

  // What it iterates over, unlabelled: the type line above already says this is a loop, so a
  // "Loop Over" label beside it only repeats the word.
  //
  // `sourcePath` only — deliberately not falling back to a saved flow's `contentPath`, which holds
  // the editor's JSON-array encoding that toDisplayValue has no branch for and would print raw.
  const source = toDisplayString(step?.sourcePath);
  if (!isEmptyInputValue(source)) {
    rows.push({ labelKey: "", display: toDisplayValue(source) });
  }

  const config = step?.loopConfig || {};
  if (config.parallelProcessing) {
    rows.push({
      labelKey: "Common.ThreadCount",
      display: {
        text: String(config.threadCount ?? DEFAULT_LOOP_THREAD_COUNT),
        tagKind: "",
      },
    });
  }

  return rows;
}

function toConditionRow(condition: any): ConditionRow {
  const operator = condition?.operator ?? condition?.condition;
  const left = conditionTakesLeftValue(operator)
    ? toDisplayString(condition?.left ?? condition?.firstInput)
    : "";
  const right = conditionTakesRightValue(operator)
    ? toDisplayString(condition?.right ?? condition?.secondInput)
    : "";

  return {
    rule: toDisplayString(condition?.rule),
    openParen: Boolean(condition?.open_paren ?? condition?.leftParen),
    closeParen: Boolean(condition?.close_paren ?? condition?.rightParen),
    left: toDisplayValue(left),
    operatorLabelKey: conditionOperatorLabelKey(operator),
    right: toDisplayValue(right),
  };
}

/**
 * One entry per router branch.
 *
 * A branch is the fallback when it is flagged `default`, when its only row is a `DEFAULT` operator,
 * or when it carries no rows at all — the preview snapshot has used each of those shapes.
 */
export function summarizeRouterBranches(step: any): BranchSummary[] {
  const routes = Array.isArray(step?.routes) ? step.routes : [];

  return routes.map((route: any) => {
    const conditions = Array.isArray(route?.filter) ? route.filter : [];
    const isDefault =
      Boolean(route?.default) ||
      conditions.length === 0 ||
      conditions.every((condition: any) =>
        isDefaultCondition(condition?.operator ?? condition?.condition)
      );

    return {
      // Left blank rather than defaulted here: naming is user-facing, and no util in this folder
      // pulls in i18n. The component supplies a translated fallback.
      name: (typeof route?.name === "string" && route.name.trim()) || "",
      isDefault,
      rows: isDefault ? [] : conditions.map(toConditionRow),
    };
  });
}

export function isRouterStep(step: any): boolean {
  return Array.isArray(step?.routes) && step.routes.length > 0;
}

/**
 * The rows of a step's own filter — the gate on everything that runs after it.
 *
 * Distinct from `summarizeRouterBranches`, which describes how a router picks a branch. Accepts
 * both the authored key (`conditions`) and the persisted one (`filter`), since the preview canvas
 * is fed the wizard's spec while a saved flow uses the backend's name.
 */
export function summarizeStepFilter(step: any): ConditionRow[] {
  // Same guard for both names, so precedence does not silently depend on which one is empty.
  const rows = [step?.filter, step?.conditions].find(
    (candidate) => Array.isArray(candidate) && candidate.length
  );
  return rows ? rows.map(toConditionRow) : [];
}

export interface DelaySummary {
  /** Exactly what was configured, in the unit the backend stores. */
  raw: string;
  /** Compact duration for the readable line — "2s", "1.5s", "2min". */
  duration: string;
}

/**
 * A step's trailing pause.
 *
 * `0` is deliberately treated as no delay: `applyDelay` only emits `.delay(n)` for a positive
 * value, so a zero would advertise a pause that never happens.
 */
export function summarizeStepDelay(step: any): DelaySummary | null {
  const ms = Number(step?.delay);
  if (!Number.isFinite(ms) || ms <= 0) {
    return null;
  }

  let duration: string;
  if (ms < 1000) {
    duration = `${ms}ms`;
  } else if (ms < 60000) {
    // Trim a trailing ".0" so 2000 reads "2s" rather than "2.0s".
    duration = `${Number((ms / 1000).toFixed(1))}s`;
  } else {
    duration = `${Number((ms / 60000).toFixed(1))}min`;
  }

  return { raw: `${ms} ms`, duration };
}

/**
 * Whether there is anything worth popping a card for — drives the tooltip's `disabled`.
 *
 * Inputs only. A delay or a filter is not an input, and each already has its own badge on the
 * canvas carrying its own card, so a step with nothing but those stays silent on hover rather than
 * opening a card that would have to be headed "Inputs" while listing none.
 */
export function hasStepSummary(step: any): boolean {
  if (isRouterStep(step)) {
    return summarizeRouterBranches(step).length > 0;
  }
  // A loop's settings are its inputs — it has no parameters of its own, and what it iterates over
  // is otherwise only visible inside the Loop Settings drawer.
  if (isLoopStep(step)) {
    return summarizeLoop(step).length > 0;
  }
  return summarizeStepInputs(step).length > 0;
}
